import * as React from 'react';
import { Field, FieldProps } from '../Field';
import { callAll } from '../helper/fp';
import { isNil } from '../helper/typeguard';
import { EmailInput, EmailInputProps } from './EmailInput';

export type EmailFieldProps = {
  /** validation status of the field, is you wish to control this props, pass down null when there is no value */
  status?: 'success' | 'error' | 'warning' | null;
} & EmailInputProps &
  Omit<FieldProps, 'status'>;

class EmailFieldComponent extends React.Component<
  EmailFieldProps & { innerRef: React.Ref<HTMLInputElement> },
  { status: 'success' | 'error' | undefined }
> {
  static defaultProps: Partial<EmailFieldProps> = {
    errorHelpText: 'Den indtastede e-mail er ikke gyldig.',
  };

  state = {
    status: undefined,
  };

  isControl = (propKey: keyof EmailFieldProps) => this.props[propKey] !== undefined;

  getStatus = () => (this.isControl('status') ? this.props.status || undefined : this.state.status);

  setComponentStatus = (isValid: boolean | undefined) =>
    !this.isControl('status') &&
    this.setState({
      status: isNil(isValid) ? undefined : isValid ? 'success' : 'error',
    });

  render() {
    const {
      label,
      wrapperClassName,
      status,
      maxWidth,
      compact,
      helpText,
      alwaysShowHelpText,
      errorHelpText,
      innerRef,
      onValidityChange,
      tooltip,
      tooltipAvoidPositions,
      ...inputProps
    } = this.props;

    return (
      <Field
        status={this.getStatus()}
        label={label}
        wrapperClassName={wrapperClassName}
        maxWidth={maxWidth}
        compact={compact}
        alwaysShowHelpText={alwaysShowHelpText}
        helpText={helpText}
        errorHelpText={errorHelpText}
        tooltip={tooltip}
        tooltipAvoidPositions={tooltipAvoidPositions}
        required={inputProps.required}
        inputId={inputProps.id}
        renderInput={({ getInputProps }) => (
          <EmailInput
            {...getInputProps({
              ...inputProps,
              onValidityChange: callAll(this.setComponentStatus, onValidityChange),
              ref: innerRef,
            })}
          />
        )}
      />
    );
  }
}

/**
 * EmailField is a composition of `Field` and `EmailInput`.
 * If you wish to have more control, you can compose them yourself.
 */
export const EmailField = React.forwardRef<HTMLInputElement, EmailFieldProps>((props, forwardedRef) => (
  <EmailFieldComponent {...(props as Exclude<keyof EmailFieldProps, 'ref'>)} innerRef={forwardedRef} />
));
